<template>
  <div :id="`graph--position${graphID}`">
    <div class="echarts">
      <IEcharts :id="`graphID${graphID}`" :key="reloadGraph" :loading="loading" :option="bar" :theme="theme" :resizable="resizable" @ready="onReady" @click="onClickHandler" />
    </div>
    <div :id="`overlay${graphID}`" @click="toggleOverlay">
    </div>
  </div>
</template>

<script>
import {
  EventBus
} from '@/services/event-bus'
import {
  colorMixin
} from '@/util/mixin/index.js'
import IEcharts from 'vue-echarts-v3/src/full.js'

export default {
  components: {
    IEcharts
  },
  mixins: [colorMixin],
  props: {
    graphID: {
      type: [Number, String],
      default: 1
    },
    series: {
      type: Array,
      required: true,
      default: function () {
        return []
      }
    },
    visualMap: {
      type: Object,
      default: function () {
        return {}
      }
    },
    xAxis: {
      type: [Array, Object]
    },
    xAxisData: {
      type: Array,
      default: function () {
        return []
      }
    },
    yAxis: {
      type: Array,
      default: function () {
        return [{
          type: 'value'
        }]
      }
    },
    title: {
      type: Object,
      default: function () {
        return {}
      }
    },
    grid: {
      type: Array,
      default: function () {
        return []
      }
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    legendPosition: {
      type: Object,
      default: function () {
        return {}
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      required: true
    },
    isOnClickValueRequired: {
      type: Boolean,
      default: false
    },
    isHoverFix: {
      type: Boolean,
      default: true
    },
    toolBoxPosition: {
      type: Object,
      default: function () {
        return {
          right: 100
        }
      }
    },
    isMagicType: {
      type: Boolean,
      default: true
    },
    isDownload: {
      type: Boolean,
      default: true
    },
    popUpWidth: {
      type: String,
      default: '10vw'
    }
  },
  data() {
    return {
      resizable: true,
      legendPerCol: 14,
      bar: {
        backgroundColor: '',
        legend: {
          show: true,
          type: 'scroll',
          width: "75%",
          bottom: 50,
          textStyle: {
            color: ''
          }
        },
        grid: [{
          left: '15%',
          right: '10%',
          bottom: 100
        }],
        tooltip: {
          trigger: 'axis',
          textStyle: {
            color: '#fff'
          },
          position: function (pos, params, dom, rect, size) {
            let obj = {
              top: 0
            }
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 5
            return obj
          },
          formatter: (params, ticket, callback) =>
            this.formatter(params, ticket, callback)
        },
        title: {
          textStyle: {}
        },
        xAxis: {
          data: []
        },
        toolbox: {
          showTitle: false,
          // right: 100,
          // top: 20,
          // For changing graph type and download
          feature: {
            magicType: {
              show: this.isMagicType,
              type: ['line', 'bar']
            },
            saveAsImage: {
              show: this.isDownload
            }
          }
        },
        axisPointer: {
          link: [{
            xAxisIndex: 'all'
          }]
        },
        dataZoom: [{
          type: 'inside',
          start: 0,
          end: 100
        },
        {
          start: 0,
          end: 100,
          type: 'slider',
          handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '70%',
          backgroundColor: '',
          dataBackground: {
            lineStyle: {
              color: '#fff',
              width: 1,
              opacity: 0.2
            },
            areaStyle: {
              color: '#1c2747'
            }
          }
        }
        ],
        yAxis: [{}],
        series: []
      },
      dataLength: null,
      reloadGraph: 0
    }
  },
  computed: {
    backgroundColor() {
      return this.$vuetify.theme.primary
    },
    textColor() {
      return this.theme === 'dark' ? '#ffffff' : '#000000'
    }
  },
  mounted() {
    document.querySelector(`#graph--position${this.graphID}`).style.position = 'relative'
    Object.assign(document.querySelector(`#overlay${this.graphID}`).style, {
      position: 'fixed',
      display: 'none',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 2,
      cursor: 'pointer'
    })
    this.dataLength = this.series.length
    let tempSeries = []
    const excludedType = ['pie', 'heatmap']
    const positionKeyList = ['top', 'bottom', 'left', 'right']
    positionKeyList.forEach(el => {
      if (this.toolBoxPosition[el]) {
        this.bar.toolbox[el] = this.toolBoxPosition[el]
      }
    })
    if (!excludedType.includes(this.series[0].type)) {
      this.series.forEach((el, index) => {
        if (el.xAxisIndex !== undefined) {
          this.$set(this.bar.dataZoom[index], 'xAxisIndex', [0, 1])
        }
        if (el.itemStyle) {
          el['itemStyle'].color = this.colors[index]
        } else {
          el['itemStyle'] = {
            color: this.colors[index]
          }
        }
        tempSeries.push(el)
      })
    } else {
      tempSeries = [...this.series]
    }

    if (this.series[0].type === 'heatmap') {
      this.$set(this.bar, 'visualMap', {})
      this.bar['visualMap'] = this.visualMap

      delete this.bar.dataZoom
      this.bar.tooltip = {
        type: 'item',
        position: 'top',
        backgroundColor: '#293257',
        textStyle: {
          color: '#fff'
        }
      }
    }
    this.bar.series = [...tempSeries]

    if (this.grid.length) {
      this.bar.grid = [...this.grid]
    }
    this.bar.yAxis = this.yAxis

    if (this.series[0].type !== 'pie') {
      this.$set(this.bar, 'xAxis', {})
      if (this.xAxisData.length) {
        this.$set(this.bar.xAxis, 'data', [])
        this.bar.xAxis.data = [...this.xAxisData]
      } else {
        if (this.xAxis) {
          this.bar.xAxis = this.xAxis
        }
      }
    }
    this.bar.title = this.title
    this.bar.legend['show'] = this.showLegend
    const legendPosKeys = Object.keys(this.legendPosition)
    if (legendPosKeys.length) {
      legendPosKeys.forEach((key) => {
        this.bar.legend[key] = this.legendPosition[key]
      })
    }

    if (this.series[0].type === 'pie') {
      this.bar.tooltip = {
        type: 'item',
        position: 'top',
        backgroundColor: '#293257',
        textStyle: {
          color: '#fff'
        }
      }
      this.bar.legend['right'] = '33%'
      this.bar.legend.bottom = 0
      delete this.bar.tooltip.position
      delete this.bar.dataZoom
      delete this.bar.xAxis
      delete this.bar.yAxis
    }

    this.graphColorHandler()
  },
  destroyed() {
    this.bar.series = []
    this.bar.xAxis.data = []
  },
  methods: {
    graphColorHandler() {
      this.$set(this.bar, 'backgroundColor', this.backgroundColor)
      this.$set(this.bar.title, 'textStyle', {})
      this.$set(this.bar.title.textStyle, 'color', this.textColor)

      const excludedType = ['pie', 'heatmap']
      if (!excludedType.includes(this.series[0].type)) {
        for (let y = 0; y < this.bar.yAxis.length; y++) {
          this.$set(this.bar.yAxis[y], 'nameTextStyle', {})
          this.$set(this.bar.yAxis[y].nameTextStyle, 'color', this.textColor)

          this.$set(this.bar.yAxis[y], 'axisLine', {})
          this.$set(
            this.bar.yAxis[y].axisLine,
            'show',
            true
          )
          this.$set(this.bar.yAxis[y].axisLine, 'lineStyle', {})
          this.$set(
            this.bar.yAxis[y].axisLine.lineStyle,
            'color',
            this.textColor
          )

          this.$set(this.bar.yAxis[y], 'axisLabel', {})
          this.$set(this.bar.yAxis[y].axisLabel, 'color', this.textColor)
        }

        if (!this.bar.xAxis.axisLine) {
          this.$set(this.bar.xAxis, 'axisLine', {})
          this.$set(this.bar.xAxis.axisLine, 'show', true)
          this.$set(this.bar.xAxis.axisLine, 'lineStyle', {})
        }
        this.$set(this.bar.xAxis.axisLine.lineStyle, 'color', this.textColor)
        if (!this.bar.xAxis.axisLabel) {
          this.$set(this.bar.xAxis, 'axisLabel', {})
        }
        this.$set(this.bar.xAxis.axisLabel, 'color', this.textColor)
        this.bar.dataZoom[1].backgroundColor =
          this.theme === 'dark' ? '#293258' : '#C7C7C7'
        this.bar.dataZoom[1].dataBackground.lineStyle.color =
          this.theme === 'dark' ? '#fff' : '#000'
        this.bar.legend.textStyle.color = this.textColor
      }
    },
    toggleOverlay() {
      let overlay = document.getElementById(`overlay${this.graphID}`)
      overlay.nextSibling.style.display = 'none'
      overlay.style.display = 'none'
    },
    fixTableHandler(event) {
      let timeNode = document.createTextNode(this.timeWhileHover)
      let overlay = document.getElementById(`overlay${this.graphID}`)
      let hoverTable = document.getElementById(`hoverTable${this.graphID}`)
      let parentNode = document.createElement('div')
      parentNode.appendChild(timeNode)

      const maxWidth = hoverTable.firstElementChild.offsetWidth - 32
      hoverTable.style.maxWidth = `${maxWidth}px`
      hoverTable.style.minWidth = hoverTable.style.width

      const styles = {
        display: 'block',
        borderStyle: 'solid',
        position: 'absolute',
        'background-color': '#293257',
        'border-width': '0px',
        'border-color': '#293257',
        'border-radius': '4px',
        color: 'white',
        padding: '5px',
        top: '0px',
        zIndex: 2,
        whiteSpace: 'nowrap'
      }

      let offsetLeft = document.getElementById(`graphID${this.graphID}`).getBoundingClientRect().left
      let actualGraphWidth = document.getElementById(`graphID${this.graphID}`).clientWidth

      if (event.clientX > (actualGraphWidth / 2) + offsetLeft) {
        styles['left'] = '10px'
      } else {
        styles['right'] = '10px'
      }

      Object.assign(parentNode.style, styles)
      parentNode.appendChild(hoverTable)
      overlay.style.display = 'block'
      overlay.insertAdjacentElement('afterend', parentNode)
    },
    onClickHandler(event) {
      if (this.isOnClickValueRequired) {
        console.log('graph-click event emitted')
        EventBus.$emit('graph-click', {
          xAxisData: this.bar.xAxis.data,
          eventIndex: event.dataIndex
        })
      }
    },
    formatter(params, ticket, callback) {
      let res = `<div style="font-weight: bold">
        <div>
          ${params[0].name}
        </div>`
      res += `
          <div style="font-size: 12px; color: #FFC107">
            [${this.$t('Note')}: ${this.$t('Click to make it stick')}]
          </div>
        </div>
        <div id="hoverTable${this.graphID}" style="resize:horizontal; pointer-events: all"><table>`
      this.timeWhileHover = params[0].name
      let column =
        params.length / this.legendPerCol
          ? params.length / this.legendPerCol
          : 1
      for (let i = 0; i < params.length; i++) {
        let k
        res += '<tr>'
        for (let j = 0; j < column; j++) {
          if (params[i + j] !== undefined) {
            k = i + j
            res +=
              `<td style="text-align: center; width: 10vw; background-color: ${
                params[k].color
              }; color: ${
                this.luminosity(params[i].color) ? 'white' : 'black'
              }">` +
              params[k].seriesName +
              ': ' +
              (params[k].value !== undefined || params[k].value != null
                ? params[k].value
                : 'N/A') +
              `</td>`
          }
        }
        res += '</tr>'
        i = k
      }
      res += '</table></div>'

      this.$nextTick(() => {
        document.querySelector(`#hoverTable${this.graphID}`).parentNode.style.backgroundColor =
          '#293257'
        document.querySelector(`#hoverTable${this.graphID}`).style.width = this.popUpWidth
        document.querySelector(`#hoverTable${this.graphID}`).style.overflowX = 'auto'
      })
      return res
    },
    onReady(instance, ECharts) {
      if (this.isHoverFix) {
        document.querySelector(`#graphID${this.graphID}`).addEventListener('click', (event) => {
          this.fixTableHandler(event)
        })
      }
    },
    luminosity(color) {
      let colorHex = color.substring(1)
      let rgb = parseInt(colorHex, 16)
      let r = (rgb >> 16) & 0xff
      let g = (rgb >> 8) & 0xff
      let b = (rgb >> 0) & 0xff
      let brightness = (r * 299 + g * 587 + b * 114) / 1000

      return brightness < 130
    }
  },
  watch: {
    theme() {
      this.graphColorHandler()
    },
    series() {
      this.dataLength = this.series.length
      let tempSeries = []
      const excludedType = ['pie', 'heatmap']
      if (!excludedType.includes(this.series[0].type)) {
        this.series.map((el, index) => {
          if (el.xAxisIndex !== undefined) {
            this.$set(this.bar.dataZoom[index], 'xAxisIndex', [0, 1])
          }
          el['itemStyle'] = {
            color: this.colors[index]
          }
          tempSeries.push(el)
        })
      } else {
        tempSeries = [...this.series]
      }
      this.reloadGraph += 1
      this.bar.series = [...tempSeries]
    },
    title() {
      this.bar.title = this.title
    },
    xAxis: {
      handler(val) {
        this.bar.xAxis = val
      },
      deep: true
    },
    yAxis: {
      handler(val) {
        this.bar.yAxis = val
      },
      deep: true
    },
    xAxisData: {
      handler(val) {
        this.bar.xAxis.data = [...val]
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.echarts {
  height: 100%;
}
</style>
